import { supabase } from '../lib/supabaseClient';
import type { RealtimeChannel } from '@supabase/supabase-js';

/**
 * Private Chat Service
 * 1-on-1 messaging between team members
 */

export interface PrivateMessage {
  id: string;
  created_at: string;
  organization_id: string;
  sender_id: string;
  recipient_id: string;
  content: string;
  sender_name: string;
  recipient_name: string;
  is_read: boolean;
  is_edited: boolean;
  image_url?: string | null;  // Optional image attachment
}

export interface TeamMember {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: string;
  unread_count?: number;
}

/**
 * Get all team members in the user's organization
 */
export const getTeamMembers = async (): Promise<TeamMember[]> => {
  console.log('[PrivateChat] 👥 Fetching team members...');
  
  // Use RPC function to get team members with user details
  const { data, error } = await supabase
    .rpc('get_team_members_with_details');

  if (error) {
    console.error('[PrivateChat] ❌ Failed to fetch team members:', error);
    throw error;
  }

  // Transform to TeamMember format
  const teamMembers: TeamMember[] = (data || []).map((member: any) => ({
    user_id: member.user_id,
    email: member.email,
    first_name: member.first_name,
    last_name: member.last_name,
    full_name: member.full_name,
    role: member.role,
    unread_count: 0 // Will be updated separately
  }));

  console.log('[PrivateChat] ✅ Fetched', teamMembers.length, 'team members');
  return teamMembers;
};

/**
 * Get unread message count for each team member
 */
export const getUnreadCounts = async (): Promise<Record<string, number>> => {
  console.log('[PrivateChat] 📊 Fetching unread counts...');
  
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Get all unread messages where user is recipient
  const { data, error } = await supabase
    .from('private_messages')
    .select('sender_id')
    .eq('recipient_id', user.id)
    .eq('is_read', false)
    .eq('is_deleted', false);

  if (error) {
    console.error('[PrivateChat] ❌ Failed to fetch unread counts:', error);
    return {};
  }

  // Count messages by sender
  const counts: Record<string, number> = {};
  data?.forEach(msg => {
    counts[msg.sender_id] = (counts[msg.sender_id] || 0) + 1;
  });

  console.log('[PrivateChat] ✅ Unread counts:', counts);
  return counts;
};

/**
 * Send a private message to a team member
 * @param recipientId - User ID of recipient
 * @param recipientName - Display name of recipient
 * @param content - Message text content
 * @param imageUrl - Optional image URL from storage
 */
export const sendPrivateMessage = async (
  recipientId: string,
  recipientName: string,
  content: string,
  imageUrl?: string
): Promise<PrivateMessage> => {
  console.log('[PrivateChat] 📤 Sending private message to:', recipientName);
  
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Get user's organization
  const { data: membership, error: membershipError } = await supabase
    .from('organization_members')
    .select('organization_id')
    .eq('user_id', user.id)
    .eq('is_active', true)
    .single();

  if (membershipError || !membership) {
    throw new Error('No organization found');
  }

  // Get sender name
  const firstName = user.user_metadata?.first_name || '';
  const lastName = user.user_metadata?.last_name || '';
  const senderName = `${firstName} ${lastName}`.trim() || user.email || 'Anonymous';

  // Prepare message data
  const messageData: any = {
    organization_id: membership.organization_id,
    sender_id: user.id,
    recipient_id: recipientId,
    content,
    sender_name: senderName,
    recipient_name: recipientName
  };
  
  // Add image URL if provided
  if (imageUrl) {
    messageData.image_url = imageUrl;
    console.log('[PrivateChat] 📷 Attaching image:', imageUrl);
  }

  console.log('[PrivateChat] 🔍 Inserting message with data:', {
    organization_id: messageData.organization_id,
    sender_id: messageData.sender_id,
    recipient_id: messageData.recipient_id,
    content: messageData.content.substring(0, 50),
    sender_name: messageData.sender_name,
    recipient_name: messageData.recipient_name
  });

  // Insert message
  const { data, error } = await supabase
    .from('private_messages')
    .insert(messageData)
    .select()
    .single();

  if (error) {
    console.error('[PrivateChat] ❌ Failed to send message:', error);
    throw error;
  }

  console.log('[PrivateChat] ✅ Message sent successfully:', data.id);
  return data;
};

/**
 * Get conversation with a specific user
 */
export const getConversation = async (otherUserId: string, limit: number = 100): Promise<PrivateMessage[]> => {
  console.log('[PrivateChat] 📥 Fetching conversation with:', otherUserId);
  
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Fetch messages between current user and other user
  // Get all messages where (sender=me AND recipient=them) OR (sender=them AND recipient=me)
  const { data, error } = await supabase
    .from('private_messages')
    .select('*')
    .eq('is_deleted', false)
    .order('created_at', { ascending: true })
    .limit(limit * 2); // Get more to filter client-side
  
  // Filter to only messages between these two users
  const filteredData = data?.filter(msg => 
    (msg.sender_id === user.id && msg.recipient_id === otherUserId) ||
    (msg.sender_id === otherUserId && msg.recipient_id === user.id)
  ).slice(0, limit);

  if (error) {
    console.error('[PrivateChat] ❌ Failed to fetch conversation:', error);
    throw error;
  }

  console.log('[PrivateChat] ✅ Fetched', filteredData?.length || 0, 'messages in conversation');
  return filteredData || [];
};

/**
 * Mark messages as read
 */
export const markMessagesAsRead = async (senderId: string): Promise<void> => {
  console.log('[PrivateChat] ✅ Marking messages as read from:', senderId);
  
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('private_messages')
    .update({ is_read: true })
    .eq('sender_id', senderId)
    .eq('recipient_id', user.id)
    .eq('is_read', false);

  if (error) {
    console.error('[PrivateChat] ❌ Failed to mark as read:', error);
    throw error;
  }

  console.log('[PrivateChat] ✅ Messages marked as read');
};

/**
 * Subscribe to private messages with a specific user
 */
export const subscribeToPrivateMessages = (
  otherUserId: string,
  onNewMessage: (message: PrivateMessage) => void,
  onError?: (error: Error) => void
): Promise<RealtimeChannel | null> => {
  return new Promise(async (resolve) => {
    console.log('[PrivateChat] 🔌 Setting up private subscription with:', otherUserId);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('[PrivateChat] ❌ Not authenticated for subscription');
        resolve(null);
        return;
      }

      // Subscribe to all messages involving current user
      // RLS will filter to only show messages user is allowed to see
      const channel = supabase
        .channel(`private_messages:${user.id}:${otherUserId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'private_messages'
            // No filter - let RLS handle it
          },
          (payload) => {
            const msg = payload.new as PrivateMessage;
            // Only process if message involves this conversation
            if ((msg.sender_id === user.id && msg.recipient_id === otherUserId) ||
                (msg.sender_id === otherUserId && msg.recipient_id === user.id)) {
              console.log('[PrivateChat] 📨 New private message received:', msg);
              onNewMessage(msg);
            }
          }
        )
        .subscribe((status) => {
          console.log('[PrivateChat] 🔌 Private subscription status:', status);
          if (status === 'SUBSCRIBED') {
            console.log('[PrivateChat] ✅ Private subscription active');
          } else if (status === 'CHANNEL_ERROR') {
            console.error('[PrivateChat] ❌ Subscription error - check RLS policies and realtime settings');
          }
        });

      resolve(channel);
    } catch (error) {
      console.error('[PrivateChat] ❌ Subscription error:', error);
      if (onError) onError(error as Error);
      resolve(null);
    }
  });
};

/**
 * Unsubscribe from private messages
 */
export const unsubscribeFromPrivateMessages = async (channel: RealtimeChannel): Promise<void> => {
  console.log('[PrivateChat] 🔌 Unsubscribing from private messages...');
  await supabase.removeChannel(channel);
  console.log('[PrivateChat] ✅ Unsubscribed from private messages');
};
